import type { DateValue } from "react-aria-components";

export type ValidateExportDateRangeOptions = {
	/** When true, dates are ignored and validation passes. */
	pullAllSinceStart?: boolean;
	/**
	 * When true (default), both dates are required unless pullAllSinceStart.
	 * When false (vehicles), both dates may be omitted; if one is set, both must be.
	 */
	isRequired?: boolean;
	/** Enforced for events and trips only. */
	enforceMaxOneYear?: boolean;
	maxValue?: DateValue;
};

export function formatDateValueToApi(
	date: DateValue | null | undefined
): string | null {
	if (!date) {
		return null;
	}

	const year = date.year.toString().padStart(4, "0");
	const month = date.month.toString().padStart(2, "0");
	const day = date.day.toString().padStart(2, "0");

	return `${year}-${month}-${day}`;
}

export function validateExportDateRange(
	start: DateValue | null | undefined,
	end: DateValue | null | undefined,
	options: ValidateExportDateRangeOptions | boolean = {}
): string | null {
	// Backward-compatible overload: third arg used to be `pullAllSinceStart: boolean`.
	const normalizedOptions: ValidateExportDateRangeOptions =
		typeof options === "boolean" ? { pullAllSinceStart: options } : options;

	const {
		pullAllSinceStart = false,
		isRequired = true,
		enforceMaxOneYear = false,
		maxValue,
	} = normalizedOptions;

	if (pullAllSinceStart) {
		return null;
	}

	const hasStart = Boolean(start);
	const hasEnd = Boolean(end);

	if (!(hasStart || hasEnd)) {
		if (isRequired) {
			return "As datas de início e fim do período são obrigatórias.";
		}
		return null;
	}

	if (hasStart !== hasEnd) {
		return "As datas de início e fim devem ser informadas juntas.";
	}

	if (!(start && end)) {
		return null;
	}

	if (start.compare(end) > 0) {
		return "A data inicial deve ser anterior ou igual à data final.";
	}

	if (maxValue && (start.compare(maxValue) > 0 || end.compare(maxValue) > 0)) {
		return "As datas não podem ser posteriores a hoje.";
	}

	if (enforceMaxOneYear && start.compare(end.subtract({ years: 1 })) < 0) {
		return "O intervalo de datas não pode ultrapassar um ano.";
	}

	return null;
}
